import React, { Suspense, useEffect } from 'react'
import { Toaster } from '@/components/ui/toaster'
import { SkipLink } from '@/components/accessibility/skip-link'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { ProgressProvider } from '@/contexts/ProgressContext'
import { LastStepProvider } from '@/contexts/LastStepContext'
import { ApplicationLoadingProvider } from '@/contexts/ApplicationLoadingContext'
import { QueryParamsProvider } from '@/contexts/QueryParamsContext'
import { buildEnvironmentConfig, envLog } from '@/config/env'
import { ReadOnlyService } from '@yardi/yardi-react-framework'
import { registerLicense } from '@syncfusion/ej2-base';
import { MenuWorkflowMapper } from '@/lib/MenuWorkflowMapper';
import { ApplicationLoadingScreen } from '@/components/ui/ApplicationLoadingScreen'
import { environment } from '@/config/environment';
import { useAuth } from '@/lib/auth'
import { setConfigurableFormsConfig } from '@ysi/configurable-forms-react'
import '@ysi/configurable-forms-react/styles'
import { apiClientSecure } from '@/lib/api/index'
import { getPropertyId } from '@/hooks/useApiSequencing'
import { RouteProtection } from '@/components/route-protection/RouteProtection'
const licenseKey = import.meta.env.VITE_SYNCFUSION_LICENSE_KEY;
registerLicense(licenseKey);

// Lazy load the main routing component to reduce initial bundle
const LazyAppRoutes = React.lazy(() => import('./AppRoutes'))
const LazyHelpWidget = React.lazy(() => import('@/components/help-widget').then(m => ({ default: m.HelpWidget })))

// Minimal loading component
const AppLoading = () => (
  <ApplicationLoadingScreen message="Loading application..." />
)

function App() {
  // Initialize MenuWorkflowMapper

  const { user, isLoading } = useAuth();

  useEffect(() => {
      if (user && !isLoading) {
        MenuWorkflowMapper.initialize();
      }
    }, [user, isLoading]);
  
  // Initialize framework's ReadOnlyService
   React.useEffect(() => {
     ReadOnlyService.initialize(false); // Initialize with readonly disabled by default
     envLog.info(`App initialized in ${buildEnvironmentConfig.env} mode`);
   }, []);

  // Configurable forms (versioned package) – same package used by OLE and Guarantor Portal
  useEffect(() => {
    try {
      const siteName = () => localStorage.getItem('sitename')?.replace(/"/g, '') ?? '';
      setConfigurableFormsConfig({
        apiClient: apiClientSecure,
        getPropertyId,
        getSiteName: () => siteName() || null,
        completedStepPageName: 'customformsections',
        buildRedirectUrl: ({ siteName: s, completedStepPageName }) =>
          `/onlineleasingstudent/content3/${s}/ole/${completedStepPageName}`,
        getSectionBaseUrl: (stepName) =>
          `/onlineleasingstudent/content3/${siteName()}/ole/${stepName || 'customformsections'}`,
        stepLabels: {
          studentdetails: 'Student Info',
          customformsections: 'Applicant Info',
          petsinformation: 'Pets',
          vehiclesinformation: 'Vehicles',
          emergencycontact: 'Emergency Contact',
        },
      });
    } catch (error) {
      console.error('Failed to initialize configurable forms config:', error);
    }
  }, []);

  return (
    <QueryParamsProvider>
      <ThemeProvider>
        <ProgressProvider>
          <LastStepProvider>
            <ApplicationLoadingProvider>
              <RouteProtection>
                {/* Skip to main content link for keyboard accessibility (matches guarantor portal) */}
                <SkipLink />
                <Suspense fallback={<AppLoading />}>
                <LazyAppRoutes />
                </Suspense>
                <Toaster />
                <Suspense fallback={null}>
                  <LazyHelpWidget />
                </Suspense>
              </RouteProtection>
            </ApplicationLoadingProvider>
          </LastStepProvider>
        </ProgressProvider>
      </ThemeProvider>
    </QueryParamsProvider>
  )
}

export default App